import React, { Suspense, useState, useMemo, useEffect, lazy } from "react";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import lightBlue from "@mui/material/colors/lightBlue";
import blue from "@mui/material/colors/blue";
// import lightPink from "@mui/material/colors/cyan";
import pink from "@mui/material/colors/pink";
import red from "@mui/material/colors/red";
// import ls from "local-storage";
import * as ls from "local-storage";
import { ThemeType } from "../utils/storage";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SafeArea } from "capacitor-plugin-safe-area";
import { CapacitorSQLite, SQLiteConnection } from "@capacitor-community/sqlite";

const App = lazy(() => import("./App"));

const AppWrapper = () => {
  // const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const correctTheme = (t: string): ThemeType => {
    if (["system", "dark", "light"].includes(t)) return t as ThemeType;
    return "system";
  };

  const getTheme = () => {
    const t = ls.get<ThemeType>("theme");
    // console.log('t', t);
    if (t && t.substring(0, 1) === '"') return correctTheme(JSON.parse(t));
    if (t) return correctTheme(t);
    return "system";
  };
  const [modeFromSettings, setModeFromSettings] = useState(getTheme);

  // console.log("LS", ls.get());
  const setThemeMode = (m: ThemeType) => {
    ls.set("theme", m);
    // localStorage.setItem("theme", m);
    setModeFromSettings(m);
  };
  // console.log('modeFromPlugin', modeFromPlugin);
  // const themeMode = ls('themeMode') || 'system';

  const isDark = useMemo(() => {
    if (modeFromSettings === "dark") return true; // app set to dark
    if (modeFromSettings === "light") return false; // app set to light
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    )
      return true; // css set to dark
    return false;
  }, [modeFromSettings]);
  // console.log('isDark', isDark, modeFromPlugin, modeFromSettings);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: isDark ? lightBlue : blue,
          secondary: isDark
            ? {
                light: "#f48fb1",
                main: "#f06292",
                dark: "#ec407a",
              }
            : pink,
          error: red,
          contrastThreshold: 3,
          tonalOffset: 0.2,
          mode: isDark ? "dark" : "light",
        },
        components: {
          // MuiButton: {
          //   styleOverrides: {
          //     root: {
          //       borderRadius: 15,
          //       textTransform: "none",
          //     },
          //   },
          // },
          // MuiDialog: {
          //   styleOverrides: {
          //     paper: {
          //       borderRadius: 15,
          //     },
          //   },
          // },
          // MuiTextField: {
          //   styleOverrides: {
          //     root: {
          //       borderRadius: 15,
          //     },
          //   },
          // },
          // MuiOutlinedInput: {
          //   styleOverrides: {
          //     root: {
          //       borderRadius: 15,
          //     },
          //   },
          // },
        },
        // change theme.shape.borderRadius
        shape: {
          borderRadius: 15,
        },
      }),
    [isDark],
  );

  // useEffect(() => {
  //   if (
  //     typeof StatusBar !== "undefined" &&
  //     StatusBar &&
  //     StatusBar.backgroundColorByHexString
  //   ) {
  //     StatusBar.backgroundColorByHexString(theme.palette.background.paper);
  //     if (isDark) {
  //       StatusBar.styleLightContent();
  //     } else {
  //       StatusBar.styleDefault();
  //     }
  //   }
  // });
  // console.log('theme', theme);
  // debug={JSON.stringify({ isDark, modeFromPlugin, modeFromSettings })}

  // One-time migration: if localStorage is empty (no grids), try to read from
  // the old Cordova SQLite backup (ls.db) and populate localStorage from it.
  useEffect(() => {
    const MIGRATION_FLAG = "sqlite_migration_done";
    if (ls.get(MIGRATION_FLAG)) return;
    ls.set(MIGRATION_FLAG, true);

    // Only attempt if localStorage appears empty (no game history)
    if ((ls.get<string[]>("grids") || []).length > 0) return;

    const runMigration = async () => {
      try {
        const sqlite = new SQLiteConnection(CapacitorSQLite);
        const db = await sqlite.createConnection(
          "ls", // Cordova stored the file as ls.db
          false,
          "no-encryption",
          1,
          false,
        );
        await db.open();
        const result = await db.query("SELECT key, value FROM kvstore");
        await db.close();
        await sqlite.closeConnection("ls", false);

        if (result.values && result.values.length > 0) {
          result.values.forEach(
            ({ key, value }: { key: string; value: string }) => {
              try {
                localStorage.setItem(key, value); // value is already JSON-stringified
              } catch {
                // ignore individual key failures
              }
            },
          );
          console.log(
            `[Migration] Restored ${result.values.length} keys from SQLite backup`,
          );
        }
      } catch (e) {
        // SQLite not available or db not found — normal for fresh installs
        console.log("[Migration] SQLite migration skipped:", e);
      }
    };

    runMigration();
  }, []);

  useEffect(() => {
    // Inject safe area insets as CSS custom properties.
    // env(safe-area-inset-*) is unreliable in Android WebViews; native values are more accurate.
    const applyInsets = (insets: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    }) => {
      for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(
          `--safe-area-inset-${key}`,
          `${value}px`,
        );
      }
    };
    SafeArea.getSafeAreaInsets()
      .then(({ insets }) => applyInsets(insets))
      .catch(() => {});
    let listenerHandle: { remove: () => Promise<void> } | null = null;
    SafeArea.addListener("safeAreaChanged", ({ insets }) => applyInsets(insets))
      .then((handle) => {
        listenerHandle = handle;
      })
      .catch(() => {});
    return () => {
      listenerHandle?.remove();
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.paper;
    StatusBar.setBackgroundColor({ color: "#000000" }).catch(() => {});
    StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  }, [theme, isDark]);
  return (
    <Suspense
      fallback={
        <Backdrop open>
          <CircularProgress />
        </Backdrop>
      }
    >
      <ThemeProvider theme={theme}>
        <App setThemeMode={setThemeMode} appTheme={modeFromSettings} />
      </ThemeProvider>
    </Suspense>
  );
};

export default AppWrapper;
